import { Image } from 'expo-image';
import type { ImageProps } from 'expo-image';
import { useAppTakeoverCartItem } from 'features/app-takeover/hooks/use-app-takeover';
import { useAppTakeoverCart } from 'features/app-takeover/hooks/use-app-takeover-cart';
import type { TakeoverCartItemImageFit } from 'features/app-takeover/types';
import { iosCartBoldWeight } from 'features/cart/styles';
import { ImageBackground, StyleSheet, View } from 'react-native';
import type { ImageBackgroundProps } from 'react-native';

import { Typography } from 'components/ui';
import { primitive, spacing } from 'styles/constants';

interface ConfiguredTakeoverHeaderProps {
  itemUrl: string;
  title: string;
}

const getExpoContentFit = (fit?: TakeoverCartItemImageFit): ImageProps['contentFit'] => {
  if (fit === 'stretch') {
    return 'fill';
  }
  if (fit === 'center') {
    return 'none';
  }
  return fit;
};

const getBackgroundResizeMode = (
  fit?: TakeoverCartItemImageFit
): ImageBackgroundProps['resizeMode'] => fit ?? 'stretch';

const withoutUndefined = <Value extends object>(value: Value): Partial<Value> =>
  Object.fromEntries(Object.entries(value).filter(([, child]) => child !== undefined)) as Partial<
    Value
  >;

export const ConfiguredTakeoverHeader = ({ itemUrl, title }: ConfiguredTakeoverHeaderProps) => {
  const itemTakeoverConfig = useAppTakeoverCartItem(itemUrl);
  const { fontFamily } = useAppTakeoverCart();
  const config = itemTakeoverConfig?.cartItemHeader;

  if (!config) {
    return null;
  }

  const imageSource = config.image?.source ?? itemTakeoverConfig?.cartItemImage;
  const backgroundImageSource =
    config.backgroundImage ?? itemTakeoverConfig?.cartItemBackgroundImage;
  const { image, layout, subtitle, title: titleConfig } = config;
  const subtitleContent = subtitle ? (
    <Typography
      numberOfLines={1}
      style={[
        styles.subtitle,
        withoutUndefined({
          color: subtitle.color,
          fontSize: subtitle.fontSize,
          lineHeight: subtitle.lineHeight,
        }),
      ]}
      testID="legacy-cart-takeover-subtitle"
    >
      {subtitle.text}
    </Typography>
  ) : null;
  const content = (
    <>
      {imageSource && (
        <Image
          contentFit={getExpoContentFit(image?.fit)}
          source={imageSource}
          style={[
            styles.image,
            image &&
              withoutUndefined({
                width: image.width,
                height: image.height,
                marginTop: image.marginTop,
                marginRight: image.marginRight,
                marginBottom: image.marginBottom,
                marginLeft: image.marginLeft,
              }),
          ]}
          testID="legacy-cart-takeover-image"
        />
      )}
      <View style={styles.textContainer}>
        <Typography
          ellipsizeMode="tail"
          numberOfLines={1}
          style={[
            styles.title,
            fontFamily ? { fontFamily } : undefined,
            titleConfig &&
              withoutUndefined({
                color: titleConfig.color,
                fontSize: titleConfig.fontSize,
                lineHeight: titleConfig.lineHeight,
                maxWidth: titleConfig.maxWidth,
              }),
          ]}
          testID="cart-item-label"
        >
          {title}
        </Typography>
        {subtitleContent}
      </View>
    </>
  );
  const headerStyle = [
    styles.header,
    layout &&
      withoutUndefined({
        height: layout.height,
        paddingHorizontal: layout.paddingHorizontal,
        paddingVertical: layout.paddingVertical,
        gap: layout.gap,
      }),
  ];

  if (backgroundImageSource) {
    return (
      <ImageBackground
        imageStyle={styles.backgroundImage}
        resizeMode={getBackgroundResizeMode(config.backgroundImageFit)}
        source={backgroundImageSource}
        style={headerStyle}
        testID="legacy-cart-takeover-header"
      >
        {content}
      </ImageBackground>
    );
  }

  return (
    <View style={headerStyle} testID="legacy-cart-takeover-header">
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundImage: {
    width: '100%',
    height: '100%',
    borderTopLeftRadius: primitive.$borderRadius,
    borderTopRightRadius: primitive.$borderRadius,
  },
  header: {
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: primitive.$blackOpacity10,
    borderTopLeftRadius: primitive.$borderRadius,
    borderTopRightRadius: primitive.$borderRadius,
    flexDirection: 'row',
    overflow: 'hidden',
    alignItems: 'center',
  },
  image: {
    aspectRatio: 1,
    height: 50,
    marginLeft: spacing.$3,
  },
  textContainer: {
    flex: 1,
    gap: 2,
    minWidth: 0,
    paddingRight: spacing.$13_5,
  },
  title: {
    color: primitive.$bbqBrown,
    flexShrink: 1,
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 14,
    ...iosCartBoldWeight,
  },
  subtitle: {
    color: primitive.$bbqBrown,
    flexShrink: 1,
    fontFamily: primitive.fontFamily.$body,
    fontSize: 10,
    lineHeight: 12,
  },
});
